import { useEffect, useRef } from "react";
import type { OrgUser } from "@/hooks/use-org-users";

interface MentionDropdownProps {
  users: OrgUser[];
  filter: string;
  selectedIndex: number;
  onSelect: (user: OrgUser) => void;
  position: { top: number; left: number };
}

export function MentionDropdown({
  users,
  filter,
  selectedIndex,
  onSelect,
  position,
}: MentionDropdownProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = users.filter((u) => {
    const q = filter.toLowerCase();
    return (
      u.displayName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  });

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (filtered.length === 0) return null;

  return (
    <div
      ref={listRef}
      className="absolute z-50 max-h-40 w-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
      style={{ top: position.top, left: position.left }}
    >
      {filtered.map((user, i) => (
        <button
          key={user.uid}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(user);
          }}
          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
            i === selectedIndex
              ? "bg-primary/10 text-primary"
              : "text-foreground hover:bg-muted"
          }`}
        >
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-bold text-muted-foreground">
            {user.displayName
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{user.displayName}</div>
            <div className="truncate text-[10px] text-muted-foreground">
              {user.role}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/** Get the filtered list for external use (e.g., keyboard nav bounds). */
export function filterMentionUsers(users: OrgUser[], filter: string): OrgUser[] {
  const q = filter.toLowerCase();
  return users.filter(
    (u) =>
      u.displayName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q),
  );
}
